/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// This file co-locates the `badgeVariants` cva() helper with the Badge
// component per the shadcn/ui convention. Fast Refresh needs a file to
// export components only; splitting `badgeVariants` into a sibling file
// is a design-system refactor that is out of scope for the lint-cleanup
// pass. Falling back to a full page reload on edits here is acceptable —
// the file changes infrequently.
/* eslint-disable react-refresh/only-export-components */
'use client';

import type { ComponentProps } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
    'inline-flex items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-xs px-2 py-0.5 text-xs leading-4 font-semibold uppercase tracking-button transition-[color,background-color,border-color,box-shadow] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-focus disabled:pointer-events-none disabled:opacity-50',
    {
        variants: {
            variant: {
                primary: '',
                secondary: '',
                destructive: '',
                outline: '',
                ghost: '',
                warning: '',
                info: '',
                success: '',
            },
            state: {
                primary: '',
                secondary: '',
            },
            shape: {
                label: 'min-h-5',
                number: 'min-h-5 min-w-5 rounded-full px-1',
            },
        },
        compoundVariants: [
            {
                variant: 'primary',
                state: 'primary',
                className: 'bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
            },
            { variant: 'primary', state: 'secondary', className: 'bg-background text-primary' },
            {
                variant: 'secondary',
                state: 'primary',
                className: 'bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
            },
            { variant: 'secondary', state: 'secondary', className: 'bg-background text-secondary-foreground' },
            {
                variant: 'destructive',
                state: 'primary',
                className:
                    'bg-destructive text-white [a&]:hover:bg-destructive/90 dark:focus-visible:ring-destructive/40',
            },
            { variant: 'destructive', state: 'secondary', className: 'bg-background text-destructive' },
            {
                variant: 'outline',
                state: 'primary',
                className: 'border border-input bg-background text-foreground [a&]:hover:bg-secondary',
            },
            { variant: 'outline', state: 'secondary', className: 'border border-input bg-background text-foreground' },
            { variant: 'ghost', state: 'primary', className: 'bg-muted text-foreground [a&]:hover:bg-muted/80' },
            { variant: 'ghost', state: 'secondary', className: 'bg-background text-muted-foreground' },
            {
                variant: 'warning',
                state: 'primary',
                className: 'bg-warning text-warning-foreground [a&]:hover:bg-warning/90',
            },
            { variant: 'warning', state: 'secondary', className: 'bg-background text-warning' },
            { variant: 'info', state: 'primary', className: 'bg-info text-info-foreground [a&]:hover:bg-info/90' },
            { variant: 'info', state: 'secondary', className: 'bg-background text-info' },
            {
                variant: 'success',
                state: 'primary',
                className: 'bg-success text-success-foreground [a&]:hover:bg-success/90',
            },
            { variant: 'success', state: 'secondary', className: 'bg-background text-success' },
        ],
        defaultVariants: {
            variant: 'primary',
            state: 'primary',
            shape: 'label',
        },
    }
);

export type BadgeProps = ComponentProps<'span'> &
    VariantProps<typeof badgeVariants> & {
        asChild?: boolean;
    };

export default function Badge({ className, variant, state, shape, asChild = false, ...props }: BadgeProps) {
    const Comp = asChild ? Slot : 'span';

    return <Comp data-slot="badge" className={cn(badgeVariants({ variant, state, shape }), className)} {...props} />;
}

export { badgeVariants };
